import React, { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Icon, ICON_SIZE } from '../ui/icons';
import { useAuth } from '../auth/AuthContext';
import { useT } from '../i18n';
import { useProperty } from './PropertyContext';
import { NAV_SECTIONS, ALL_NAV_ITEMS } from './navigation';
import NotificationBell from '../features/notifications/NotificationBell';
import OnboardingWalkthrough from '../features/onboarding/OnboardingWalkthrough';
import VersionUpdateWatcher from './VersionUpdateWatcher';
import './AppShell.css';

function initials(user) {
  const a = (user?.firstName || '').trim();
  const b = (user?.lastName || '').trim();
  if (a || b) return `${a[0] || ''}${b[0] || ''}`.toUpperCase();
  return (user?.email || '?').slice(0, 2).toUpperCase();
}

function PropertySwitcher() {
  const { properties, activePropertyId, activeProperty, setActiveProperty, loading, reload } = useProperty();
  const t = useT();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function onDocClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // Re-fetch on open so properties created (or access granted) after this tab
  // loaded show up without a full page refresh.
  useEffect(() => {
    if (open) reload().catch(() => {});
  }, [open, reload]);

  const label = activeProperty?.name || (activePropertyId ? t('Property') : t('All properties'));

  const choose = (id) => {
    setActiveProperty(id);
    setOpen(false);
  };

  return (
    <div className="pp-prop" ref={ref}>
      <button
        type="button"
        className="pp-prop__btn"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        title={t('Active property')}
      >
        <Icon.Building size={16} />
        <span className="pp-prop__name">{label}</span>
        <Icon.ChevronDown size={14} />
      </button>
      {open && (
        <div className="pp-prop__menu" role="menu">
          <button
            type="button"
            className={`pp-prop__item${!activePropertyId ? ' is-active' : ''}`}
            role="menuitemradio"
            aria-checked={!activePropertyId}
            onClick={() => choose(null)}
          >
            <span>{t('All properties')}</span>
            {!activePropertyId && <Icon.Check size={16} />}
          </button>
          <hr className="pp-prop__sep" />
          {loading && properties.length === 0 && <div className="pp-prop__empty">{t('Loading…')}</div>}
          {!loading && properties.length === 0 && <div className="pp-prop__empty">{t('No properties yet')}</div>}
          {properties.map((p) => (
            <button
              key={p.id}
              type="button"
              className={`pp-prop__item${activePropertyId === p.id ? ' is-active' : ''}`}
              role="menuitemradio"
              aria-checked={activePropertyId === p.id}
              onClick={() => choose(p.id)}
            >
              <span className="pp-prop__item-main">
                <span className="pp-prop__item-name">{p.name}</span>
                {(p.baseCurrency || p.timezone) && (
                  <span className="pp-prop__item-meta">
                    {[p.baseCurrency, p.timezone].filter(Boolean).join(' · ')}
                  </span>
                )}
              </span>
              {activePropertyId === p.id && <Icon.Check size={16} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const { hasPermission } = useProperty();
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const acctRef = useRef(null);

  const onboardingPending = !!user && !user.onboardingCompletedAt;

  useEffect(() => {
    if (user?.language) document.documentElement.lang = user.language;
  }, [user?.language]);

  const active = ALL_NAV_ITEMS.find((i) =>
    i.end ? location.pathname === i.to : location.pathname.startsWith(i.to) && i.to !== '/',
  );
  const title = t(active?.label || 'Dashboard');

  useEffect(() => {
    function onDocClick(e) {
      if (acctRef.current && !acctRef.current.contains(e.target)) setMenuOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="ps-scope app-root pp-shell">
      <aside className="pp-rail" aria-label="Sidebar">
        <div className="pp-brand">
          <span className="pp-brand__mark">P</span>
          <span className="pp-brand__name">Procurement</span>
        </div>
        <nav className="pp-nav" aria-label="Primary">
          {NAV_SECTIONS.map((section, idx) => {
            const items = section.items.filter((item) => hasPermission(item.perm));
            if (items.length === 0) return null;
            return (
              <div className="pp-nav__section" key={section.label || `s-${idx}`}>
                {section.label && <div className="pp-nav__label">{t(section.label)}</div>}
                {items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    title={t(item.label)}
                    className={({ isActive }) => {
                      const matched = item.match?.some((p) => location.pathname === p || location.pathname.startsWith(`${p}/`));
                      return `pp-nav__link${isActive || matched ? ' is-active' : ''}`;
                    }}
                  >
                    <item.Icon size={ICON_SIZE.nav} />
                    <span>{t(item.label)}</span>
                  </NavLink>
                ))}
              </div>
            );
          })}
        </nav>
      </aside>

      <header className="pp-topbar">
        <span className="pp-topbar__title">{title}</span>
        <span className="pp-topbar__spacer" />
        <div className="pp-topbar__right">
          <PropertySwitcher />
          <NotificationBell />
          <div className="pp-acct" ref={acctRef}>
            <button
              type="button"
              className="pp-acct__btn"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((o) => !o)}
            >
              <span className="pp-acct__avatar">{initials(user)}</span>
            </button>
            {menuOpen && (
              <div className="pp-acct__menu" role="menu">
                <div className="pp-acct__head">
                  <div className="pp-acct__name">
                    {user?.firstName} {user?.lastName}
                  </div>
                  <div className="pp-acct__email">{user?.email}</div>
                </div>
                <hr className="pp-acct__sep" />
                <button type="button" className="pp-acct__item" role="menuitem" onClick={() => { setMenuOpen(false); navigate('/account'); }}>
                  <Icon.User size={16} />
                  <span>{t('My account')}</span>
                </button>
                <button type="button" className="pp-acct__item" role="menuitem" onClick={() => { setMenuOpen(false); setShowTour(true); }}>
                  <Icon.BookOpen size={16} />
                  <span>{t('Getting started')}</span>
                </button>
                <button type="button" className="pp-acct__item" role="menuitem" onClick={handleLogout}>
                  <Icon.LogOut size={16} />
                  <span>{t('Sign out')}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="pp-main">
        <Outlet />
      </main>

      {onboardingPending && <OnboardingWalkthrough />}
      {!onboardingPending && showTour && (
        <OnboardingWalkthrough reviewMode onClose={() => setShowTour(false)} />
      )}

      <VersionUpdateWatcher />
    </div>
  );
}
